/**
 * Subscribe activation poll (TMMAPP-0028). After a 202 card challenge the SPA never replays
 * subscribe — it waits for the pay-api feed via bounded `MeProvider.refetch` polls.
 * Nothing about an attempt is persisted (no sessionStorage, no replay token).
 */

import { getMe } from '@/api/me';

export const SUBSCRIBE_POLL_MS = 2_000;
export const SUBSCRIBE_POLL_WINDOW_MS = 60_000;

function sleep(ms: number, signal?: AbortSignal): Promise<'ok' | 'aborted'> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve('aborted');
      return;
    }
    const timer = setTimeout(() => resolve('ok'), ms);
    const onAbort = () => {
      clearTimeout(timer);
      resolve('aborted');
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Poll `refetch` every `intervalMs` until `GET /me` reports SUBSCRIBER, the window elapses,
 * or `signal` aborts. Never polls faster than `SUBSCRIBE_POLL_MS` by default.
 */
export async function awaitSubscriberActivation(
  refetch: () => Promise<unknown>,
  options?: {
    signal?: AbortSignal;
    windowMs?: number;
    intervalMs?: number;
  },
): Promise<'activated' | 'timeout' | 'aborted'> {
  const windowMs = options?.windowMs ?? SUBSCRIBE_POLL_WINDOW_MS;
  const intervalMs = options?.intervalMs ?? SUBSCRIBE_POLL_MS;
  const signal = options?.signal;
  const deadline = Date.now() + windowMs;

  while (true) {
    if (signal?.aborted) return 'aborted';
    await refetch();
    const me = await getMe();
    if (me.tier === 'SUBSCRIBER') return 'activated';
    if (Date.now() >= deadline) return 'timeout';
    const waited = await sleep(intervalMs, signal);
    if (waited === 'aborted' || signal?.aborted) return 'aborted';
  }
}
